import React from "react";
import styled, { css, keyframes } from "styled-components";
import { useTranslation } from "react-i18next";
import useRightSidebar from "hooks/useRightSidebar";
import usePluginEntities from "hooks/usePluginEntities";
import Icon from "components/common/Icon";
import IconButton from "components/common/IconButton";
import {
  ANIMATION_DURATION,
  SidebarContainer,
  SidebarRow,
  SidebarContentArea,
  SidebarHeader,
  SidebarTitle
} from "./SidebarStyles";
const COLLAPSED_WIDTH = 36;
const fade = keyframes`
  from {
    opacity: 0;
  }
`;
const Container = styled(SidebarContainer)`
  position: sticky;
  top: 0;
`;
const CollapsedContainer = styled.div(
  ({ theme }) => css`
    width: ${COLLAPSED_WIDTH}px;
    min-width: ${COLLAPSED_WIDTH}px;
    flex-shrink: 0;
    align-self: stretch;
    position: sticky;
    top: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: ${theme.colors.global.contentBackground};
  `
);
const CollapsedTitleArea = styled.div(
  ({ theme }) => css`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    cursor: pointer;
    overflow: hidden;

    :hover {
      background-color: ${theme.colors.variant.lightest.default};
    }
  `
);
const CollapsedTitle = styled.span(
  ({ theme }) => css`
    writing-mode: vertical-rl;
    text-orientation: mixed;
    transform: rotate(180deg);
    font-size: ${theme.fonts.size.small};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-height: 100%;
    color: ${theme.colors.text.primary};
    user-select: none;

    animation: ${fade} ${ANIMATION_DURATION} ease-in-out;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `
);
const NavigationButtons = styled.div`
  display: flex;
  gap: 4px;
  margin-right: 8px;
`;
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
const CollapseButton = styled.button(
  ({ theme }) => css`
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: ${theme.colors.text.primary};
    display: flex;
    align-items: center;

    :hover {
      color: ${theme.colors.variant.dark.default};
    }
  `
);
const RightSidebar = () => {
  var _a, _b;
  const {
    content,
    width,
    isCollapsed,
    closeSidebar,
    collapseSidebar,
    expandSidebar,
    goBack,
    goForward,
    canGoBack,
    canGoForward
  } = useRightSidebar();
  const sidebarComponents = usePluginEntities("sidebar.components");
  const { t } = useTranslation("common");
  if (!content) {
    return null;
  }
  if (isCollapsed) {
    return /* @__PURE__ */ React.createElement(
      CollapsedContainer,
      {
        role: "complementary",
        "aria-label": t("right_sidebar.sidebar_label_collapsed", { title: content.title })
      },
      /* @__PURE__ */ React.createElement(
        IconButton,
        {
          name: "close",
          title: t("right_sidebar.close"),
          onClick: closeSidebar,
          "aria-label": t("right_sidebar.close")
        }
      ),
      /* @__PURE__ */ React.createElement(CollapsedTitleArea, { onClick: expandSidebar, title: t("right_sidebar.expand") }, /* @__PURE__ */ React.createElement(CollapsedTitle, null, content.title))
    );
  }
  const ContentComponent = (_b = content.component) != null ? _b : (_a = sidebarComponents.find((c) => c.key === content.componentKey)) == null ? void 0 : _a.component;
  if (!ContentComponent) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(
    Container,
    {
      $width: width,
      role: "complementary",
      "aria-label": t("right_sidebar.sidebar_label", { title: content.title })
    },
    /* @__PURE__ */ React.createElement(SidebarRow, { className: "content" }, /* @__PURE__ */ React.createElement(SidebarHeader, null, /* @__PURE__ */ React.createElement(NavigationButtons, null, /* @__PURE__ */ React.createElement(
      IconButton,
      {
        name: "arrow_back",
        title: t("right_sidebar.go_back"),
        onClick: goBack,
        disabled: !canGoBack,
        "aria-label": t("right_sidebar.go_back_label")
      }
    ), /* @__PURE__ */ React.createElement(
      IconButton,
      {
        name: "arrow_forward",
        title: t("right_sidebar.go_forward"),
        onClick: goForward,
        disabled: !canGoForward,
        "aria-label": t("right_sidebar.go_forward_label")
      }
    )), /* @__PURE__ */ React.createElement(SidebarTitle, { id: "sidebar-title" }, content.title), /* @__PURE__ */ React.createElement(HeaderActions, null, /* @__PURE__ */ React.createElement(
      CollapseButton,
      {
        type: "button",
        title: t("right_sidebar.collapse"),
        onClick: collapseSidebar,
        "aria-label": t("right_sidebar.collapse")
      },
      /* @__PURE__ */ React.createElement(Icon, { name: "collapse_content" })
    ), /* @__PURE__ */ React.createElement(
      IconButton,
      {
        name: "close",
        title: t("right_sidebar.close"),
        onClick: closeSidebar,
        "aria-label": t("right_sidebar.close")
      }
    ))), /* @__PURE__ */ React.createElement(SidebarContentArea, { "aria-labelledby": "sidebar-title" }, /* @__PURE__ */ React.createElement(ContentComponent, { ...content.props || {} })))
  );
};
export default RightSidebar;
